import * as React from 'react';

import { useParams } from 'react-router';

import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import {
  assetOwners,
  getOwnershipTotal,
} from '@webapp/store/ownership/selectors';
import {
  CreateOwnership,
  GetOwnerships,
  RemoveOwnership,
  UpdateOwnershipOrder,
} from '@webapp/store/ownership/action-creators';
import {
  updateOwnershipsWithSplit,
  removeOwnershipFromArray,
  updateOwnershipIndex,
} from '@webapp/utils/split-changes';

import OwnershipCtrl from '@webapp/controllers/track/ownership';
import scrollOnError from '@webapp/utils/release/scroll-on-error';

import Box from '@components/layout/box';
import MotionOpacity from '@components/motion/motion-opacity';
import FormContainer from '@components/layout/form-container';
import SidebarContainer from '@components/layout/sidebar-container';
import SmallNotification from '@components/small-notification';
import { OwnershipLockHint } from '@webapp/controllers/user-profile/change-split/ownership-lock-hint';
import { BoxHeader } from '@webapp/controllers/user-profile/change-split/box-header';
import BoxBottom from '@webapp/controllers/user-profile/change-split/box-bottom';
import { SidebarContent } from '@webapp/controllers/user-profile/change-split/sidebar-content';
import { OwnershipState } from '@webapp/store/ownership/state';
import CatalogRelease from '@common/types/catalog/catalog-release';
import { GET_OWNERSHIPS } from '@webapp/store/ownership/actions';
import { LoadingModalCtrl } from '@webapp/controllers/loader-modal';
import { Ownership } from '@services/ownership/types/ownership';
import SimpleArtist from '@common/types/artist/simple-artist';

interface ChangeSplitProps {
  getOwnership: (assetId: string) => GetOwnerships;
  onRemove: (trackId: string, ownershipId: string) => RemoveOwnership;
  updateOwnershipOrder: (
    assetId: string,
    newOrder: string[]
  ) => UpdateOwnershipOrder;
  invalidMasterForm: () => boolean;
  invalidPublishingForm: () => boolean;
  createOwnership: (owner: Ownership) => CreateOwnership;
  owners: OwnershipState;
  allReleases: CatalogRelease[];
  loading: boolean;
  user: any;
  artistList: SimpleArtist[];
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createOwnership: (owner: Ownership) => dispatch(new CreateOwnership(owner)),
  getOwnership: (assetId: string) => dispatch(new GetOwnerships(assetId)),
  onRemove: (trackId: string, ownershipId: string) =>
    dispatch(new RemoveOwnership(trackId, ownershipId)),
  updateOwnershipOrder: (assetId: string, newOrder: string[]) =>
    dispatch(new UpdateOwnershipOrder(assetId, newOrder)),
});

const mapStateToProps = (state: AppState) => {
  const activeTrackId = state.router.location.pathname.split('/')[5];
  const invalidMasterForm = () => {
    return getOwnershipTotal(state.ownership, activeTrackId, 'OWNER') !== 100;
  };
  const invalidPublishingForm = () => {
    return getOwnershipTotal(state.ownership, activeTrackId, 'WRITER') !== 100;
  };
  return {
    invalidMasterForm: invalidMasterForm,
    invalidPublishingForm: invalidPublishingForm,
    owners: state.ownership,
    allReleases: state.catalog.releases.items,
    loading: !!state.ui.loading[GET_OWNERSHIPS],
    user: state.user.profile,
    artistList: state.artist.myArtists,
  };
};

export const ChangeSplit: React.FC<ChangeSplitProps> = ({
  getOwnership,
  createOwnership,
  user,
  artistList,
  owners,
  allReleases,
  loading,
}) => {
  const { releaseId, songName, trackId, type, lock, assetType } = useParams<{
    releaseId: string;
    songName: string;
    trackId: string;
    type: string;
    lock: string;
    assetType: string;
  }>();

  const lockPeriod = lock === 'TRUE';

  const activeRelease = allReleases.find((release) => release.id === releaseId);

  const isRequest = useSelector(
    (state: AppState) =>
      !state.artist.myArtists.some((a) => a.id === activeRelease?.artist.id)
  );
  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );
  const [validateForm, setValidateForm] = React.useState(false);
  const [reassignSplitError, SetReassignSplitError] = React.useState(false);
  const [giveAwayError, SetgiveAwayError] = React.useState(false);
  const [excessSplitError, SetExcessSplitError] = React.useState(false);
  const [ownershipId, setOwnershipId] = React.useState('');
  const [newSplit, setNewSplit] = React.useState(0);
  const [trackIdState, setTrackIdState] = React.useState('');
  const [canEditAllOwnerships, setCanEditAllOwnerships] = React.useState(false);

  const [tabActive, setTabActive] = React.useState<
    'recordings' | 'publishing'
  >();
  const [updatedOwnerArrSplit, setUpdatedOwnerArrSplit] = React.useState<
    Ownership[]
  >([]);
  const [updatedWriterArrSplit, setUpdatedWriterArrSplit] = React.useState<
    Ownership[]
  >([]);

  React.useEffect(() => {
    if (!user) return;
    setValidateForm(false);
    if (assetType === 'track') {
      activeRelease?.tracks?.map((track) => {
        getOwnership(track.id);
      });
    }
    getOwnership(trackId);
    setTrackIdState(trackId);
    if (
      artistList.filter((a) => a.id === activeRelease?.artist.id).length > 0
    ) {
      setCanEditAllOwnerships(true);
    }
  }, [getOwnership, trackId, assetType, activeRelease, user, artistList]);

  React.useEffect(() => {
    scrollOnError();
  }, [reassignSplitError]);

  React.useEffect(() => {
    setUpdatedOwnerArrSplit(assetOwners(owners, trackId, 'OWNER'));
    setUpdatedWriterArrSplit(assetOwners(owners, trackId, 'WRITER'));
  }, [owners, trackId]);

  React.useEffect(() => {
    if (
      updatedOwnerArrSplit.reduce((memo, o) => memo + o.split, 0) > 100 ||
      updatedWriterArrSplit.reduce((memo, o) => memo + o.split, 0) > 100
    ) {
      SetExcessSplitError(true);
    } else {
      SetExcessSplitError(false);
    }
    if (
      !updatedOwnerArrSplit.some((o) => o.ownerId === user.id) &&
      !tabActive
    ) {
      setTabActive('publishing');
    }
    if (
      !updatedWriterArrSplit.some((o) => o.ownerId === user.id) &&
      !tabActive
    ) {
      setTabActive('recordings');
    }
  }, [updatedOwnerArrSplit, updatedWriterArrSplit, user, tabActive]);

  const onRemoveOwnership = (ownershipId: string) => {
    if (tabActive === 'recordings') {
      setUpdatedOwnerArrSplit(
        removeOwnershipFromArray(updatedOwnerArrSplit, ownershipId)
      );
    } else {
      setUpdatedWriterArrSplit(
        removeOwnershipFromArray(updatedWriterArrSplit, ownershipId)
      );
    }
    setTrackIdState(trackId || releaseId);
  };

  const updateSplitOnSubmit = (ownershipId: string, updatedSplit: number) => {
    if (tabActive === 'recordings') {
      setUpdatedOwnerArrSplit(
        updateOwnershipsWithSplit(
          updatedOwnerArrSplit,
          isRequest,
          ownershipId,
          updatedSplit
        )
      );
    } else {
      setUpdatedWriterArrSplit(
        updateOwnershipsWithSplit(
          updatedWriterArrSplit,
          isRequest,
          ownershipId,
          updatedSplit
        )
      );
    }
    setTrackIdState(trackId || releaseId);
  };

  const createOwnershipOnSubmit = (newOwnerShip: Ownership) => {
    createOwnership(newOwnerShip);
    if (tabActive === 'recordings') {
      setUpdatedOwnerArrSplit([...updatedOwnerArrSplit, newOwnerShip]);
    } else {
      setUpdatedWriterArrSplit([...updatedWriterArrSplit, newOwnerShip]);
    }
    setTrackIdState(trackId || releaseId);
  };

  const updateSplitRequestOnSubmit = (
    ownershipId: string,
    newSplit: number,
    trackId: string
  ) => {
    if (tabActive === 'recordings') {
      let newOwnershipArr = updatedOwnerArrSplit.map((ownerShip) => {
        if (ownerShip.id === ownershipId) {
          ownerShip.requestedSplit = newSplit;
        }
        return ownerShip;
      });
      setUpdatedOwnerArrSplit(newOwnershipArr);
    }
    if (tabActive === 'publishing') {
      let newOwnershipArr = updatedWriterArrSplit.map((ownerShip) => {
        if (ownerShip.id === ownershipId) {
          ownerShip.requestedSplit = newSplit;
        }
        return ownerShip;
      });
      setUpdatedWriterArrSplit(newOwnershipArr);
    }
    setOwnershipId(ownershipId);
    setNewSplit(newSplit);
    setTrackIdState(trackId || releaseId);
  };

  const updateOwnershipOrderOnSubmit = (oldIndex: number, newIndex: number) => {
    if (tabActive === 'recordings') {
      setUpdatedOwnerArrSplit(
        updateOwnershipIndex(updatedOwnerArrSplit, oldIndex, newIndex)
      );
    } else {
      setUpdatedWriterArrSplit(
        updateOwnershipIndex(updatedWriterArrSplit, oldIndex, newIndex)
      );
    }
    setTrackIdState(trackId || releaseId);
  };

  return (
    <>
      {!loading ? (
        <SidebarContainer>
          <SidebarContent
            activeRelease={activeRelease}
            assetType={assetType}
            releaseId={releaseId}
            trackId={trackId}
            owners={owners}
            userId={user.id}
            type={type}
            lock={lock}
            songName={songName}
          />
          <FormContainer hasSidebar={true}>
            <MotionOpacity>
              <BoxHeader
                assetType={assetType}
                tabActive={tabActive}
                canEditAllOwnerships={canEditAllOwnerships}
                recordingOwnerships={updatedOwnerArrSplit}
                publishingOwnerships={updatedWriterArrSplit}
                userId={user.id}
                tabClick={(tabName) => setTabActive(tabName)}
              />
              <Box noBorderBottom>
                <OwnershipCtrl
                  ownershipData={
                    tabActive === 'publishing'
                      ? updatedWriterArrSplit
                      : updatedOwnerArrSplit
                  }
                  title={<Translate id="OWNERSHIP.BOX.TITLE" />}
                  customInputLabel={<Translate id="OWNERSHIP.INPUT.NEW" />}
                  songName={songName}
                  trackId={trackId || releaseId}
                  releaseId={releaseId}
                  type={tabActive === 'publishing' ? 'WRITER' : 'OWNER'}
                  validate={validateForm}
                  reassignSplitError={reassignSplitError}
                  isRequest={isRequest}
                  lockedPeriod={lockPeriod}
                  assetType={assetType === 'track' ? 'TRACK' : 'MERCH'}
                  isGrey
                  setGiveawayError={SetgiveAwayError}
                  updateSplit={updateSplitOnSubmit}
                  updateSplitRequest={updateSplitRequestOnSubmit}
                  removeSplit={onRemoveOwnership}
                  updateOrder={updateOwnershipOrderOnSubmit}
                  createOwnershipOnSubmit={createOwnershipOnSubmit}
                />
              </Box>
              {assetOwners(owners, trackId).length > 1 && (
                <OwnershipLockHint
                  activeRelease={activeRelease}
                  reassignSplitError={excessSplitError}
                  giveawayError={giveAwayError}
                />
              )}
              <BoxBottom
                isRequest={isRequest}
                lockPeriod={lockPeriod}
                ownershipId={ownershipId}
                newSplit={newSplit}
                trackId={trackId}
                trackIdState={trackIdState}
                tabActive={tabActive}
                recordingOwnerships={updatedOwnerArrSplit}
                publishingOwnerships={updatedWriterArrSplit}
                onReassignSplitError={() => SetReassignSplitError(true)}
              />
            </MotionOpacity>
          </FormContainer>
          {toastNotification?.value === 'user_changes_saved' && (
            <SmallNotification
              notificationType={toastNotification.type}
              title={<Translate id={toastNotification.label} />}
            />
          )}
        </SidebarContainer>
      ) : (
        <LoadingModalCtrl loading={loading} />
      )}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeSplit);
